import { useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiGmwGmwsListRequest, GMW } from "@nens/brostar/openapi-client";

import { getApi } from "@/api/api-configuration";

import { findInListQuery } from "./helpers/find-in-list-query";
import { simplifyQueryParam } from "./helpers/simplify-query-key";

const uploadApi = getApi();

// ─── SINGLE ──────────────────────────────────────────────────────────────────

const getGmwSingleQuery = (uuid: string) => ({
  queryKey: ["gmw-single", uuid],
  queryFn: () => uploadApi.apiGmwGmwsRead({ uuid }),
});

export const useGmwSingleQueryDirectly = () => {
  const queryClient = useQueryClient();
  return (uuid: string) =>
    findInListQuery<GMW>(queryClient, ["gmw-list"], uuid, getGmwSingleQuery(uuid));
};

// ─── LIST ─────────────────────────────────────────────────────────────────────

const getGmwListQuery = (options: ApiGmwGmwsListRequest) => ({
  queryKey: ["gmw-list", simplifyQueryParam(options)],
  queryFn: () => uploadApi.apiGmwGmwsList(options),
});

export const useGmwListQuery = (options: ApiGmwGmwsListRequest) => {
  return useQuery(getGmwListQuery(options));
};

// ─── GEOJSON ─────────────────────────────────────────────────────────────────────

const getGmwGeojsonQuery = () => ({
  queryKey: ["gmw-geojson"],
  queryFn: () => uploadApi.apiGmwGeojsonList(),
});

export const useGmwGeojsonQuery = () => {
  return useQuery(getGmwGeojsonQuery());
};

// ─── INVALIDATION ─────────────────────────────────────────────────────────────

export const useInvalidateGmwQueries = () => {
  const queryClient = useQueryClient();
  return async () => {
    await queryClient.invalidateQueries({
      queryKey: ["gmw-single"],
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGmwListQuery({}).queryKey.slice(0, -1),
      refetchType: "all",
    });
    await queryClient.invalidateQueries({
      queryKey: getGmwGeojsonQuery().queryKey,
      refetchType: "all",
    });
  };
};
